/**
 * Version watchdog — the deploy-detection safety net behind the service worker.
 *
 * The SW update flow in `register.ts` only fires when the browser successfully
 * fetches a *newer* `sw.js`. Three real failures skip that entirely: a stale
 * HTTP-cached `sw.js` pinning the old worker, a browser where registration
 * never succeeded, and a worker wedged in a broken state. In all three the user
 * sits on an old build indefinitely with no signal at all.
 *
 * So this module ignores the worker and asks the server directly: `version.json`
 * is emitted at build time from `package.json` (see the `syntry:version-json`
 * plugin in vite.config.ts) and polled here, then compared against the bundle's
 * own baked-in {@link APP_VERSION} / {@link APP_BUILD}. A newer published build
 * raises {@link updateAvailable}, and `UpdateRequiredHost.vue` turns that into a
 * modal.
 *
 * **The watchdog NEVER reloads on its own.** Every reload is behind a user tap,
 * which is what makes the whole thing safe: a bad deploy, CDN lag, or a
 * version.json that is simply wrong can at worst re-show a modal — it can never
 * put a client into a reload loop. The loop guard below then stops even the
 * re-showing from becoming a nag.
 *
 * Deliberately NOT gated on service-worker support: a browser where the worker
 * never registered is exactly the case this rescues.
 */
import { readonly, ref, type Ref } from 'vue';
import { Capacitor } from '@capacitor/core';
import { APP_BUILD, APP_VERSION } from '@/utils/app-info';
import {
  applyUpdate,
  checkForUpdates,
  hasWaitingWorker,
  type UpdateCheckResult,
} from '@/pwa/register';
import { hardReload, VERSION_URL } from '@/pwa/hard-reload';
import {
  abortUpdateSession,
  beginUpdateSession,
  markRestarting,
  whenOverlayShown,
} from '@/pwa/update-session';
import { purgeUserCaches } from '@/services/offline-cache';

/** Background poll cadence. Matches the SW's own update check in register.ts. */
const CHECK_INTERVAL_MS = 60 * 60 * 1000;

/** Floor between foreground-triggered checks — tab switching is cheap, this isn't. */
const MIN_CHECK_GAP_MS = 5 * 60 * 1000;

/** How long to let the worker answer "is there an update?" before giving up on it. */
const SW_CHECK_TIMEOUT_MS = 5000;

/** Loop guard: how long to stay quiet after asking about a given published build. */
const RETRY_COOLDOWN_MS = 10 * 60 * 1000;

/** Loop guard: how many times to offer the SAME published build before giving up. */
const MAX_ATTEMPTS_PER_TARGET = 2;

/** Loop-guard record — see {@link ForceRefreshRecord}. */
const FORCE_REFRESH_KEY = 'syntry.pwa_force_refresh';

/** Plain dotted-numeric versions only — anything else compares as "equal". */
const VERSION_RE = /^\d+(\.\d+)*$/;

/** The shape of `version.json`, after validation. */
interface PublishedVersion {
  version: string;
  /** Numeric string in practice; absent or non-numeric simply disables the tiebreak. */
  build: string | null;
}

/**
 * Persisted attempt record, keyed to one published build.
 *
 * Without it, a deploy that keeps reporting a newer version while the client
 * cannot actually reach it (CDN lag, a half-propagated deploy, a bumped
 * version.json shipped next to unchanged assets) would re-prompt on every tick.
 */
interface ForceRefreshRecord {
  /** `${version}+${build}` of the published build we last acted on. */
  target: string;
  /** Epoch ms of that attempt. */
  attemptedAt: number;
  /** How many times the user has been asked to move to this same target. */
  attempts: number;
}

const available = ref(false);
const published = ref<string | null>(null);

/** The full published record behind {@link published} — needed for the build tiebreak. */
let latest: PublishedVersion | null = null;
let timer: ReturnType<typeof setInterval> | null = null;
let lastCheckAt = 0;

/** True once the server reports a build newer than the one running here. */
export const updateAvailable: Readonly<Ref<boolean>> = readonly(available);

/** The newest version the server has reported, for display ("v0.6.0 is ready"). */
export const publishedVersion: Readonly<Ref<string | null>> = readonly(published);

/**
 * Whether the watchdog runs in this runtime.
 *
 * Native is excluded for the same reason the service worker is: `cap sync`
 * packages `dist/` into the WebView, so there is no server to fetch a newer
 * build from and clearing caches could not update anything.
 */
function isSupported(): boolean {
  return (
    import.meta.env.PROD && !Capacitor.isNativePlatform() && typeof fetch !== 'undefined'
  );
}

/**
 * Compare two dotted-numeric versions.
 *
 * Exported so it can be exercised in isolation (there is no test runner — see
 * the stub-bundle pattern in `scripts/verify-mappers.mjs`). Missing segments
 * count as zero, so `1.0` equals `1.0.0`. Anything that is not plain dotted
 * digits (a pre-release tag, a git describe, junk) returns `0` — fail closed,
 * because "equal" is the answer that shows no prompt.
 */
export function compareVersions(a: string, b: string): -1 | 0 | 1 {
  const left = a.trim();
  const right = b.trim();
  if (!VERSION_RE.test(left) || !VERSION_RE.test(right)) return 0;

  const leftSegments = left.split('.');
  const rightSegments = right.split('.');
  const length = Math.max(leftSegments.length, rightSegments.length);

  for (let i = 0; i < length; i += 1) {
    const leftValue = Number.parseInt(leftSegments[i] ?? '0', 10);
    const rightValue = Number.parseInt(rightSegments[i] ?? '0', 10);
    if (leftValue > rightValue) return 1;
    if (leftValue < rightValue) return -1;
  }
  return 0;
}

/** Version first; on a tie, the numeric build number breaks it (both must parse). */
function isNewer(info: PublishedVersion): boolean {
  const byVersion = compareVersions(info.version, APP_VERSION);
  if (byVersion !== 0) return byVersion === 1;

  const publishedBuild = Number.parseInt(info.build ?? '', 10);
  const runningBuild = Number.parseInt(APP_BUILD, 10);
  if (!Number.isFinite(publishedBuild) || !Number.isFinite(runningBuild)) return false;
  return publishedBuild > runningBuild;
}

/** Loop-guard identity: version alone would miss a same-version rebuild. */
function targetOf(info: PublishedVersion): string {
  return `${info.version}+${info.build ?? ''}`;
}

/* ── The published build ─────────────────────────────────────────────────── */

/**
 * Fetch and validate `version.json`.
 *
 * Three guards, all load-bearing: the SPA rewrite in firebase.json serves
 * `/index.html` for a *missing* file **with status 200**, so `res.ok` alone
 * would hand the HTML shell to `JSON.parse`. Content-type filters that out,
 * the parse is caught, and the shape is checked before anything is trusted.
 * Every failure — offline, 404-as-HTML, malformed JSON — is a silent null.
 */
async function fetchPublished(): Promise<PublishedVersion | null> {
  try {
    const res = await fetch(VERSION_URL, { cache: 'no-store' });
    if (!res.ok) return null;
    if (!(res.headers.get('content-type') ?? '').toLowerCase().includes('json')) return null;

    const body: unknown = await res.json();
    if (typeof body !== 'object' || body === null) return null;

    const record = body as Record<string, unknown>;
    const version = typeof record.version === 'string' ? record.version.trim() : '';
    if (!VERSION_RE.test(version)) return null;

    return { version, build: typeof record.build === 'string' ? record.build : null };
  } catch {
    return null; // offline / CORS / abort — nothing to report
  }
}

/* ── Loop guard ──────────────────────────────────────────────────────────── */

/** @returns null when there is no usable record (absent, malformed, or unreadable). */
function loadForceRefresh(): ForceRefreshRecord | null {
  try {
    const raw = localStorage.getItem(FORCE_REFRESH_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ForceRefreshRecord>;
    if (typeof parsed.target !== 'string' || parsed.target === '') return null;
    return {
      target: parsed.target,
      attemptedAt: typeof parsed.attemptedAt === 'number' ? parsed.attemptedAt : 0,
      attempts: typeof parsed.attempts === 'number' ? parsed.attempts : 0,
    };
  } catch {
    return null; // private mode / corrupt value — the guard fails OPEN
  }
}

function persistForceRefresh(record: ForceRefreshRecord): void {
  try {
    localStorage.setItem(FORCE_REFRESH_KEY, JSON.stringify(record));
  } catch {
    /* no-op — worst case the user is asked again, never a loop (no auto-reload) */
  }
}

function clearForceRefresh(): void {
  try {
    localStorage.removeItem(FORCE_REFRESH_KEY);
  } catch {
    /* no-op */
  }
}

/** Stay quiet about a target we already tried twice, or tried very recently. */
function isSuppressed(target: string, now: number): boolean {
  const record = loadForceRefresh();
  // A *different* newer build always prompts — the guard only damps repeats.
  if (!record || record.target !== target) return false;
  return (
    record.attempts >= MAX_ATTEMPTS_PER_TARGET || now - record.attemptedAt < RETRY_COOLDOWN_MS
  );
}

/** Record that the user was sent to this published build — called before acting. */
function recordAttempt(): void {
  if (!latest) return;
  const target = targetOf(latest);
  const previous = loadForceRefresh();
  persistForceRefresh({
    target,
    attemptedAt: Date.now(),
    attempts: previous && previous.target === target ? previous.attempts + 1 : 1,
  });
}

/* ── Polling ─────────────────────────────────────────────────────────────── */

/**
 * One check. Throttled to {@link MIN_CHECK_GAP_MS} unless `force`.
 *
 * Reused by the About page's "Check for updates" row, which passes `force`.
 */
export async function checkPublishedVersion(force = false): Promise<void> {
  if (!isSupported()) return;

  const now = Date.now();
  if (!force && now - lastCheckAt < MIN_CHECK_GAP_MS) return;
  lastCheckAt = now;

  const info = await fetchPublished();
  // Offline / HTML shell / malformed: keep whatever we already know. A network
  // glitch must never clear a real update flag.
  if (!info) return;

  latest = info;
  published.value = info.version;

  if (!isNewer(info)) {
    // Caught up — either the update landed or the server rolled back. Reset so
    // the next genuine deploy prompts immediately instead of hitting the guard.
    available.value = false;
    clearForceRefresh();
    return;
  }

  available.value = !isSuppressed(targetOf(info), now);
}

function onVisibilityChange(): void {
  if (document.visibilityState === 'visible') void checkPublishedVersion();
}

/**
 * Start the watchdog. Idempotent — calling it while already running is a no-op.
 *
 * Called from main.ts alongside `initPwa()` rather than inside it: `initPwa()`
 * early-returns when the service worker is unsupported, which is precisely a
 * runtime the watchdog must still cover.
 */
export function initVersionCheck(): void {
  if (!isSupported() || timer !== null) return;
  timer = setInterval(() => void checkPublishedVersion(), CHECK_INTERVAL_MS);
  document.addEventListener('visibilitychange', onVisibilityChange);
  void checkPublishedVersion(true);
}

/** Stop the watchdog and detach listeners. Idempotent. */
export function stopVersionCheck(): void {
  if (timer !== null) {
    clearInterval(timer);
    timer = null;
  }
  document.removeEventListener('visibilitychange', onVisibilityChange);
}

/* ── Applying the update ─────────────────────────────────────────────────── */

export type PublishedUpdateResult =
  /** The update is under way behind the wall; the page will reload out from under the caller. */
  | 'started'
  /** Offline, or the host did not answer: NOTHING was touched and the modal is back. */
  | 'offline';

/**
 * Move this client onto the published build. Bound to the modal's "Update now".
 *
 * Prefers the service worker (it reloads every open tab to the same version via
 * `controllerchange`) and falls back to `hardReload()` when the worker is
 * absent, errored, or claims to be up to date while version.json says otherwise
 * — which is the stale-cached-`sw.js` case this whole module exists for.
 *
 * The order is load-bearing — nothing with a lasting effect runs before the path
 * is known to be viable:
 *  - offline with nothing downloaded → `'offline'` straight away, no wall;
 *  - the loop-guard attempt is recorded, and the user caches purged, only once
 *    the update is really being applied — a tap that fails no longer counts
 *    towards the two attempts after which this build's prompt is suppressed;
 *  - the hard path deletes nothing unless the host answers right now
 *    (`hardReload()`); if it does not, the wall comes down, the modal comes
 *    back, and the caller shows the offline toast.
 *
 * On `'started'` the page reloads out from under the caller: through
 * `applyUpdate()`'s `controllerchange` (or its activation fallback) on the
 * service-worker path, or straight away on the hard path. Other tabs follow via
 * `controllerchange` on the SW path, or re-prompt on their own next tick.
 */
export async function applyPublishedUpdate(): Promise<PublishedUpdateResult> {
  if (typeof navigator !== 'undefined' && navigator.onLine === false && !hasWaitingWorker()) {
    return 'offline';
  }

  available.value = false;
  beginUpdateSession({ target: latest ? { version: latest.version, build: latest.build } : null });

  if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
    // Bound the check: it waits on a worker `statechange` that a wedged install
    // may never fire. Timing out means "the worker can't tell us", which is the
    // hard path's cue.
    const result = await Promise.race([
      checkForUpdates().catch((): UpdateCheckResult => 'error'),
      new Promise<UpdateCheckResult>((resolve) => {
        window.setTimeout(() => resolve('error'), SW_CHECK_TIMEOUT_MS);
      }),
    ]);

    if (result === 'update-available') {
      recordAttempt();
      purgeUserCaches();
      // SKIP_WAITING after the wall's dwell → controllerchange → every tab
      // reloads once; applyUpdate() also owns the fallback if that never lands.
      await applyUpdate({ target: latest ? { version: latest.version, build: latest.build } : null });
      return 'started';
    }
  }

  await whenOverlayShown();
  const outcome = await hardReload({
    onCommit: () => {
      recordAttempt();
      markRestarting();
    },
  });
  if (outcome === 'unreachable') {
    abortUpdateSession();
    available.value = true;
    return 'offline';
  }
  return 'started';
}
